import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { isWebpageRoute } from "../../shared/stepConfig";
import { stepTestNamesFromCommitFiles } from "../../shared/stepTests/constants";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useQuery } from "../hooks/useQuery";
import {
  useInFlightStepTestCapture,
  useStepTestExecutions,
} from "../hooks/useStepExecutions";
import {
  type StepTestFixtureSetupRequest,
  useStepTestPin,
} from "../hooks/useStepTestPin";
import { useStepTestRows } from "../hooks/useStepTestRows";
import { pinnedExecutionIdsForStep } from "../lib/pinnedExecutions";
import { stepDirsWithStepTests } from "../lib/stepTestDirs";
import type { ParsedStep } from "../lib/parseCommitData";
import { EmptyState } from "./EmptyState";
import { PinMissingStepRow } from "./PinMissingStepRow";
import { SoftButton } from "./SoftButton";
import { StepRunPicker } from "./StepRunPicker";
import { StepTestPinPreview } from "./StepTestPinPreview";
import { StepTestStatusDot } from "./StepTestStatusDot";
import { StepTestUnpinButton } from "./StepTestUnpinButton";
import { StepTestUnpinConfirmModal } from "./StepTestUnpinConfirmModal";
import { Text } from "./Text";

type CommitFile = {
  readonly path: string;
  readonly blobHash: string;
  readonly blob?: { readonly content: string } | null;
};

const Scroll = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionHead = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Controls = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Results = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SpecRow = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 20px;
`;

const Message = styled("div")`
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const Spacer = styled("div")`
  flex: 1;
`;

const MissingBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

type Props = {
  workflowId: string;
  branchId: string;
  steps: ReadonlyArray<ParsedStep>;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
};

export function WorkflowTestingPanel({
  workflowId,
  branchId,
  steps,
  onInvestigateFailure,
  onAskSetupStepTest,
}: Props) {
  const [branch] = useQuery(queries.loadBranch({ workflowId, branchId }));
  const headCommit = branch?.headCommit ?? null;
  const commitId = headCommit?.id ?? null;
  const isMainBranch = branch?.name === "main";

  const commitFiles = useMemo(
    () => headCommit?.files ?? [],
    [headCommit?.files]
  );

  const testStepDirs = useMemo(
    () => stepDirsWithStepTests(commitFiles),
    [commitFiles]
  );

  const missingSteps = useMemo(() => {
    const withResults = new Set(testStepDirs);
    return steps.filter(
      (s) => !withResults.has(s.id) && !isWebpageRoute(s.routeType)
    );
  }, [steps, testStepDirs]);

  const hasStorageByDir = useMemo(
    () => new Map(steps.map((s) => [s.id, s.hasStorage])),
    [steps]
  );

  return (
    <Scroll>
      <StepTestsSection
        workflowId={workflowId}
        branchId={branchId}
        commitId={commitId}
        commitFiles={commitFiles}
        stepDirs={testStepDirs}
        missingSteps={missingSteps}
        hasStorageByDir={hasStorageByDir}
        isMainBranch={isMainBranch}
        onInvestigateFailure={onInvestigateFailure}
        onAskSetupStepTest={onAskSetupStepTest}
      />
    </Scroll>
  );
}

function StepTestsSection({
  workflowId,
  branchId,
  commitId,
  commitFiles,
  stepDirs,
  missingSteps,
  hasStorageByDir,
  isMainBranch,
  onInvestigateFailure,
  onAskSetupStepTest,
}: {
  workflowId: string;
  branchId: string;
  commitId: string | null;
  commitFiles: readonly CommitFile[];
  stepDirs: readonly string[];
  missingSteps: readonly ParsedStep[];
  hasStorageByDir: ReadonlyMap<string, boolean>;
  isMainBranch: boolean;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
}) {
  const z = useZero();
  const { executions } = useStepTestExecutions(workflowId, branchId);

  const running = executions.some(
    (e) => e.status === "pending" && stepDirs.includes(e.stepDir ?? "")
  );

  const handleRunAll = () => {
    if (!commitId || stepDirs.length === 0) return;
    for (const stepDir of stepDirs) {
      z.mutate(
        sharedMutators.runStepTests({
          runId: nanoid(12),
          workflowId,
          commitId,
          stepDir,
          executionId: nanoid(12),
        })
      );
    }
  };

  const hasResults = stepDirs.length > 0;

  return (
    <Section>
      <SectionHead>
        <Text variant="text-regular-md">Step tests</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Pin a run as a step test, then re-check it after edits.
        </Text>
      </SectionHead>
      {hasResults && (
        <>
          <Controls>
            <SoftButton
              variant="secondary"
              onClick={handleRunAll}
              disabled={running || !commitId}
            >
              {running ? "Running…" : "Check all step tests"}
            </SoftButton>
          </Controls>
          <Results>
            {stepDirs.map((stepDir) => (
              <StepTestGroup
                key={stepDir}
                stepDir={stepDir}
                commitFiles={commitFiles}
                executions={executions}
                workflowId={workflowId}
                branchId={branchId}
                commitId={commitId}
                isMainBranch={isMainBranch}
                hasStorage={hasStorageByDir.get(stepDir) ?? false}
                onInvestigateFailure={onInvestigateFailure}
                onAskSetupStepTest={onAskSetupStepTest}
              />
            ))}
          </Results>
        </>
      )}
      {missingSteps.length > 0 && (
        <MissingBlock>
          <Text variant="text-small-md" color="var(--text-secondary)">
            {missingSteps.length} step{missingSteps.length === 1 ? "" : "s"}{" "}
            without step tests
          </Text>
          {missingSteps.map((s) => (
            <PinMissingStepRow
              key={s.id}
              step={s}
              workflowId={workflowId}
              branchId={branchId}
              commitId={commitId}
              isMainBranch={isMainBranch}
              onAskSetupStepTest={onAskSetupStepTest}
            />
          ))}
        </MissingBlock>
      )}
      {!hasResults && missingSteps.length === 0 && (
        <EmptyState
          iconName="list-todo"
          iconSize={24}
          maxWidth={320}
          title="No steps yet"
          description="Add a step and run it, then pin a run as its step test."
        />
      )}
    </Section>
  );
}

function StepTestGroup({
  stepDir,
  commitFiles,
  executions,
  workflowId,
  branchId,
  commitId,
  isMainBranch,
  hasStorage,
  onInvestigateFailure,
  onAskSetupStepTest,
}: {
  stepDir: string;
  commitFiles: readonly CommitFile[];
  executions: Parameters<typeof useStepTestRows>[0]["executions"];
  workflowId: string;
  branchId: string;
  commitId: string | null;
  isMainBranch: boolean;
  hasStorage: boolean;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
}) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const testNames = useMemo(
    () => stepTestNamesFromCommitFiles(commitFiles, stepDir),
    [commitFiles, stepDir]
  );
  const pinnedExecutionIds = useMemo(
    () => pinnedExecutionIdsForStep(commitFiles, stepDir),
    [commitFiles, stepDir]
  );
  const { rows, rollup, couldNotRun } = useStepTestRows({
    commitFiles,
    stepDir,
    testNames,
    executions,
  });
  const inFlightCapture = useInFlightStepTestCapture(
    workflowId,
    branchId,
    isMainBranch ? null : stepDir
  );
  const isCapturingFixtures = inFlightCapture != null;
  const pin = useStepTestPin({
    stepDir,
    workflowId,
    branchId,
    commitId,
    isMainBranch,
    hasStorage,
    takenNames: testNames,
    pinnedExecutionIds,
    onAskSetupStepTest,
  });
  const [unpinName, setUnpinName] = useState<string | null>(null);

  const handleUnpin = (name: string) => {
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.unpinStepTest({
          workflowId,
          ...branchParams,
          stepDir,
          name,
        })
      )
    );
  };

  return (
    <StepGroup>
      <Row>
        <StepTestStatusDot status={isCapturingFixtures ? "running" : rollup} />
        <Text variant="text-small-md">{stepDir}</Text>
        <Spacer />
        {pin.message ? (
          <Text variant="text-small" color="var(--text-secondary)">
            {pin.message}
          </Text>
        ) : (
          <StepRunPicker
            runs={pin.runs}
            selectedId={pin.selectedId}
            onSelect={pin.setSelectedId}
            placeholder="Pin a run as a step test"
          />
        )}
      </Row>
      {couldNotRun ? (
        <SpecRow>
          <Text variant="text-small" color="var(--red-600)">
            Step tests could not run.
          </Text>
        </SpecRow>
      ) : (
        rows.map((r) => (
          <SpecRow key={r.name}>
            <Row>
              <StepTestStatusDot status={r.status} />
              <Text variant="text-small">{r.name}</Text>
              {r.status === "failed" && (
                <SoftButton
                  variant="secondary"
                  onClick={() =>
                    onInvestigateFailure({ stepDir, testName: r.name })
                  }
                >
                  Investigate failure
                </SoftButton>
              )}
              {!isMainBranch && (
                <StepTestUnpinButton
                  name={r.name}
                  onPress={() => setUnpinName(r.name)}
                />
              )}
            </Row>
            {r.message && (
              <Message>
                <Text variant="text-small" color="var(--text-secondary)">
                  {r.message}
                </Text>
              </Message>
            )}
          </SpecRow>
        ))
      )}
      {pin.selectedRun && (
        <StepTestPinPreview
          output={pin.output}
          canPin={pin.canPin}
          pinTooltip={pin.pinTooltip}
          onPin={pin.handlePin}
          onCancel={() => pin.setSelectedId(null)}
        />
      )}
      <StepTestUnpinConfirmModal
        name={unpinName}
        onClose={() => setUnpinName(null)}
        onConfirm={handleUnpin}
      />
    </StepGroup>
  );
}
